import React, { useEffect, useState, useCallback } from "react";
import { Card, SectionHeading, PrimaryButton, SecondaryButton, ErrorBanner, EmptyState } from "./ui";
import { getClientDashboard, getAstrologerDashboard } from "../api/sessionsApi";
import { isLoggedIn } from "../utils/authStorage";
import LoginModal from "./LoginModal";
import PoojaMeetingJoin from "./PoojaMeetingJoin";
import AstrologerMeetingJoin from "./AstrologerMeetingJoin";

export default function DashboardTab() {
  const [role, setRole] = useState("client"); // "client" | "astrologer"
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  // Video call join state
  const [activeCall, setActiveCall] = useState(null); // { type: "pooja" | "astrologer", bookingId, token? }

  const fetchDashboard = useCallback(() => {
    if (!isLoggedIn()) {
      setBookings([]);
      return;
    }
    setLoading(true);
    setError(null);
    const fetchFn = role === "client" ? getClientDashboard : getAstrologerDashboard;

    fetchFn()
      .then((res) => {
        setBookings(res.data || []);
      })
      .catch((err) => {
        setError(err.message || "Failed to load dashboard data.");
      })
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleJoinClick = (b) => {
    if (!b.join_url) return;

    if (b.booking_type === "pooja") {
      // Extract UUID and Token from absolute join URL
      // e.g. http://domain/api/pooja/bookings/<uuid>/join/<token>/
      const match = b.join_url.match(/\/api\/pooja\/bookings\/([^/]+)\/join\/([^/]+)\/?$/);
      if (match) {
        setActiveCall({
          type: "pooja",
          bookingId: match[1],
          token: match[2]
        });
      } else {
        setError("Invalid join link format for Pooja.");
      }
    } else if (b.booking_type === "astrologer_consultation") {
      // Extract UUID from join endpoint path
      // e.g. /api/astrologers/bookings/<uuid>/join/
      const match = b.join_url.match(/\/api\/astrologers\/bookings\/([^/]+)\/join\/?$/);
      if (match) {
        setActiveCall({
          type: "astrologer",
          bookingId: match[1]
        });
      } else {
        setError("Invalid join link format for consultation.");
      }
    }
  };

  if (activeCall) {
    if (activeCall.type === "pooja") {
      return (
        <div style={styles.callWrapper}>
          <SecondaryButton onClick={() => setActiveCall(null)} style={styles.backBtn}>
            ← Exit Call Room
          </SecondaryButton>
          <PoojaMeetingJoin bookingId={activeCall.bookingId} token={activeCall.token} />
        </div>
      );
    } else {
      return (
        <div style={styles.callWrapper}>
          <SecondaryButton onClick={() => setActiveCall(null)} style={styles.backBtn}>
            ← Exit Call Room
          </SecondaryButton>
          <AstrologerMeetingJoin bookingId={activeCall.bookingId} />
        </div>
      );
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <SectionHeading
          eyebrow="My Schedule"
          title="Synchronized Dashboard"
          subtitle="Track all your Pooja ceremonies and Astrologer consultations in one unified chronological feed."
        />

        {isLoggedIn() && (
          <div style={styles.roleToggle}>
            <button
              style={{ ...styles.toggleBtn, ...(role === "client" ? styles.toggleBtnActive : {}) }}
              onClick={() => setRole("client")}
            >
              🙋 User Feed
            </button>
            <button
              style={{ ...styles.toggleBtn, ...(role === "astrologer" ? styles.toggleBtnActive : {}) }}
              onClick={() => setRole("astrologer")}
            >
              🧑‍🏫 Pandit / Astro Feed
            </button>
          </div>
        )}
      </div>

      {!isLoggedIn() ? (
        <Card style={styles.loginCard}>
          <div style={styles.loginIcon}>🔒</div>
          <h3>Log in to View Dashboard</h3>
          <p>You need to log in to access your personal or astrologer dashboard and join live calls.</p>
          <PrimaryButton onClick={() => setShowLogin(true)}>Log In Now</PrimaryButton>
        </Card>
      ) : (
        <>
          {loading && <div style={styles.loading}>Loading dashboard items…</div>}
          <ErrorBanner message={error} />

          {!loading && !error && bookings.length === 0 && (
            <EmptyState>No upcoming bookings found on this dashboard.</EmptyState>
          )}

          {!loading && bookings.length > 0 && (
            <div style={styles.feed}>
              {bookings.map((b, idx) => {
                const isPooja = b.booking_type === "pooja";
                const date = new Date(b.scheduled_at);
                const dateStr = date.toLocaleDateString(undefined, {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                });
                const timeStr = date.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit"
                });

                return (
                  <Card key={b.id || idx} style={styles.itemCard}>
                    <div style={styles.itemHeader}>
                      <div style={styles.itemTypeWrap}>
                        <span style={styles.typeIcon}>{isPooja ? "🪔" : "✦"}</span>
                        <div>
                          <span style={styles.typeBadge}>
                            {isPooja ? "Pooja Ceremony" : "Astro Consultation"}
                          </span>
                          <h4 style={styles.itemTitle}>
                            {isPooja ? b.pooja_title || "Pooja" : b.astrologer_name || "Personal Consultation"}
                          </h4>
                        </div>
                      </div>

                      <div style={styles.dateTimeWrap}>
                        <div style={styles.dateStr}>{dateStr}</div>
                        <div style={styles.timeStr}>{timeStr}</div>
                      </div>
                    </div>

                    <div style={styles.itemFooter}>
                      <div style={styles.statusRow}>
                        {b.can_join ? (
                          <div style={styles.liveIndicator}>
                            <span style={styles.pulseDot} />
                            Live Join Window Active
                          </div>
                        ) : (
                          <div style={styles.waitingIndicator}>
                            Scheduled
                          </div>
                        )}
                      </div>

                      <PrimaryButton
                        disabled={!b.can_join}
                        onClick={() => handleJoinClick(b)}
                        style={{
                          opacity: b.can_join ? 1 : 0.6,
                          background: b.can_join
                            ? "linear-gradient(135deg, #22c55e, #15803d)"
                            : "var(--ink-soft)"
                        }}
                      >
                        🎥 Join Session
                      </PrimaryButton>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={() => {
            setShowLogin(false);
            fetchDashboard();
          }}
        />
      )}
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 800, margin: "0 auto" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 20
  },
  roleToggle: {
    display: "flex",
    background: "var(--paper-2)",
    padding: 4,
    borderRadius: 999,
    border: "1px solid var(--line)"
  },
  toggleBtn: {
    padding: "8px 16px",
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "var(--ink-soft)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    transition: "all 0.15s ease"
  },
  toggleBtnActive: {
    background: "var(--card)",
    color: "var(--ink)",
    boxShadow: "var(--shadow-card)"
  },
  loginCard: {
    textAlign: "center",
    padding: "40px 24px"
  },
  loginIcon: {
    fontSize: 48,
    marginBottom: 16
  },
  loading: { textAlign: "center", color: "var(--ink-soft)", padding: "40px 0" },
  feed: {
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  itemCard: {
    padding: "20px 22px"
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
    borderBottom: "1px solid var(--line)",
    paddingBottom: 14,
    marginBottom: 14
  },
  itemTypeWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  typeIcon: {
    fontSize: 26,
    background: "var(--paper-2)",
    width: 48,
    height: 48,
    borderRadius: "var(--radius)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  typeBadge: {
    fontSize: 11,
    textTransform: "uppercase",
    fontWeight: 700,
    letterSpacing: 0.5,
    color: "var(--terracotta)"
  },
  itemTitle: {
    margin: "2px 0 0 0",
    fontSize: 16.5,
    fontWeight: 700,
    color: "var(--ink)"
  },
  dateTimeWrap: {
    textAlign: "right"
  },
  dateStr: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--ink)"
  },
  timeStr: {
    fontSize: 12.5,
    color: "var(--ink-soft)",
    marginTop: 2
  },
  itemFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  statusRow: {
    fontSize: 13.5
  },
  liveIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#16a34a",
    fontWeight: 700
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#22c55e",
    animation: "km-pulse 1.5s infinite"
  },
  waitingIndicator: {
    color: "var(--ink-soft)",
    fontStyle: "italic"
  },
  callWrapper: {
    position: "relative",
    zIndex: 100
  },
  backBtn: {
    position: "absolute",
    top: 16,
    left: 16,
    zIndex: 1000
  }
};
